/**
 * Centralized SEO Configuration for All Pages
 * Provides route-specific meta titles, descriptions, keywords, OpenGraph,
 * Twitter cards, canonical links, and Schema.org structured data.
 */

export const DEFAULT_SEO = {
  title: "Simpel — The Cognitive AI Company",
  description:
    "Simpel transforms enterprise workflows into intelligent, autonomous cognitive systems. Empowering organizations with AI-driven operations, digital workers, and industry copilots.",
  keywords: [
    "Cognitive AI",
    "Enterprise AI",
    "AI Copilot",
    "Digital Workers",
    "Autonomous AI",
    "Process Automation",
    "Simpel AI",
  ],
  canonical: "https://simpel.ai",
  ogType: "website",
  ogImage: "https://simpel.ai/images/simpel-icon-mark.png",
  robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
};

export const ROUTE_SEO_MAP = {
  // ==========================================
  // CORE PAGES
  // ==========================================
  "/": {
    title: "Simpel — The Cognitive AI Company | Enterprise Automation",
    description:
      "Simpel transforms enterprise workflows into intelligent cognitive systems with industry AI copilots, digital workers, and autonomous process automation.",
    keywords: [
      "Simpel AI",
      "Cognitive AI Company",
      "Enterprise Automation",
      "Autonomous AI",
      "Industry AI Copilots",
      "Digital Workers",
    ],
    canonical: "/",
  },

  "/what-we-do": {
    title: "What We Do | Full-Stack Cognitive AI & Digital Transformation | Simpel",
    description:
      "Explore Simpel's end-to-end cognitive solutions: Agentic AI, Legacy Data Migration, Automation Audits, Automated Workflows, Digital Workers, and ERP Implementation.",
    keywords: [
      "Cognitive AI Solutions",
      "Enterprise AI Services",
      "Digital Transformation",
      "AI Workflows",
      "Agentic AI",
    ],
    canonical: "/what-we-do",
  },

  "/transform": {
    title: "How We Transform Enterprises | The 4-Stage Cognitive Blueprint | Simpel",
    description:
      "Discover Simpel's structured blueprint for enterprise AI adoption: Audit, Pilot, Integrate, and Scale with enterprise guardrails and measurable ROI.",
    keywords: [
      "Enterprise AI Transformation",
      "Cognitive AI Adoption",
      "AI Roadmap",
      "Digital Transformation Strategy",
    ],
    canonical: "/transform",
  },

  "/simpel-loop": {
    title: "Simpel Loop | Continuous Cognitive Optimization Engine | Simpel",
    description:
      "The self-learning operational loop connecting enterprise data, human-in-the-loop oversight, and autonomous digital workers for continuous improvement.",
    keywords: [
      "Simpel Loop",
      "Continuous Optimization AI",
      "Autonomous AI Operations",
      "Self-Learning Enterprise",
    ],
    canonical: "/simpel-loop",
  },
  "/loop": {
    title: "Simpel Loop | Continuous Cognitive Optimization Engine | Simpel",
    canonical: "/simpel-loop",
  },

  "/contact": {
    title: "Contact Us & Book Consultation | Enterprise AI Advisory | Simpel",
    description:
      "Connect with Simpel's solutions engineering team. Discuss specialized industry copilots, pilot timelines, and enterprise cognitive architecture.",
    keywords: [
      "Contact Simpel AI",
      "Book AI Consultation",
      "Enterprise AI Advisory",
      "AI Demo",
    ],
    canonical: "/contact",
  },
  "/book-consultation": {
    title: "Book an Enterprise Consultation | Simpel",
    canonical: "/contact",
  },

  "/ai-opportunities": {
    title: "AI Opportunities Explorer | Industry Copilots & Functional AI | Simpel",
    description:
      "Explore high-impact Cognitive AI opportunities across Manufacturing, Financial Services, Healthcare, Government, and Professional Services.",
    keywords: [
      "AI Opportunities",
      "Cognitive AI Use Cases",
      "Industry AI Copilots",
      "Enterprise AI Calculator",
    ],
    canonical: "/ai-opportunities",
  },

  "/cognitive": {
    title: "Cognitive AI | Industry-Specific Intelligence Copilots | Simpel",
    description:
      "Discover Simpel's suite of specialized cognitive copilots built to solve deep domain problems in manufacturing, healthcare, finance, and public services.",
    keywords: [
      "Cognitive AI",
      "Domain Copilots",
      "Specialized AI Systems",
      "Enterprise Intelligence",
    ],
    canonical: "/cognitive",
  },

  // ==========================================
  // SERVICES & SOLUTIONS
  // ==========================================
  "/agentic-ai": {
    title: "Agentic AI | Autonomous Multi-Agent Systems for Enterprise | Simpel",
    description:
      "Deploy autonomous Agentic AI that plans, decides, and executes complex enterprise tasks across systems with enterprise-grade guardrails and visibility.",
    keywords: [
      "Agentic AI",
      "Autonomous AI Agents",
      "Enterprise Multi-Agent Systems",
      "AI Task Execution",
    ],
    canonical: "/agentic-ai",
  },
  "/services/agentic-ai": { canonical: "/agentic-ai" },
  "/what-we-do/services/agentic-ai": { canonical: "/agentic-ai" },
  "/what-we-do/agentic-ai": { canonical: "/agentic-ai" },

  "/services/legacy-data-migration": {
    title: "Legacy Data Migration | AI-Powered Data Extraction & Modernization | Simpel",
    description:
      "Modernize legacy databases and enterprise systems with zero business disruption using Simpel's intelligent, AI-assisted data migration pipelines.",
    keywords: [
      "Legacy Data Migration",
      "Database Modernization",
      "ERP Data Migration",
      "AI ETL",
    ],
    canonical: "/services/legacy-data-migration",
  },
  "/what-we-do/services/legacy-data-migration": { canonical: "/services/legacy-data-migration" },
  "/what-we-do/legacy-data-migration": { canonical: "/services/legacy-data-migration" },
  "/legacy-data-migration": { canonical: "/services/legacy-data-migration" },

  "/services/automation-audit": {
    title: "Automation Audit | Identify High-Impact AI Opportunities | Simpel",
    description:
      "Discover bottlenecks, measure automation ROI, and build a strategic AI roadmap with Simpel's comprehensive enterprise automation audit.",
    keywords: [
      "Automation Audit",
      "Process Mining AI",
      "Enterprise AI Assessment",
      "Automation ROI",
    ],
    canonical: "/services/automation-audit",
  },
  "/what-we-do/services/automation-audit": { canonical: "/services/automation-audit" },
  "/what-we-do/automation-audit": { canonical: "/services/automation-audit" },
  "/automation-audit": { canonical: "/services/automation-audit" },

  "/services/automated-workflows": {
    title: "Automated Workflows | End-to-End Enterprise Process Orchestration | Simpel",
    description:
      "Connect fragmented systems and automate complex multi-step approvals, data syncs, and document routing with intelligent AI workflows.",
    keywords: [
      "Automated Workflows",
      "Process Orchestration",
      "Workflow Automation",
      "Enterprise Integration",
    ],
    canonical: "/services/automated-workflows",
  },
  "/what-we-do/services/automated-workflows": { canonical: "/services/automated-workflows" },
  "/what-we-do/automated-workflows": { canonical: "/services/automated-workflows" },
  "/automated-workflows": { canonical: "/services/automated-workflows" },

  "/services/digital-workers": {
    title: "Digital Workers | Autonomous AI Employees for Scalable Operations | Simpel",
    description:
      "Deploy 24/7 autonomous digital workers that handle invoice reconciliation, customer inquiries, claim processing, and operational reporting.",
    keywords: [
      "Digital Workers",
      "AI Workforce",
      "Autonomous RPA",
      "Intelligent Process Automation",
    ],
    canonical: "/services/digital-workers",
  },
  "/what-we-do/services/digital-workers": { canonical: "/services/digital-workers" },
  "/what-we-do/digital-workers": { canonical: "/services/digital-workers" },
  "/digital-workers": { canonical: "/services/digital-workers" },

  "/services/erp-implementations": {
    title: "ERP Implementations | AI-Native Enterprise Resource Planning | Simpel",
    description:
      "Accelerate ERP rollouts by 50% with Simpel's AI-assisted configuration, automated data mapping, and intelligent user training.",
    keywords: [
      "ERP Implementation",
      "Cloud ERP Migration",
      "AI ERP Consulting",
      "Enterprise Software Rollout",
    ],
    canonical: "/services/erp-implementations",
  },
  "/what-we-do/services/erp-implementations": { canonical: "/services/erp-implementations" },
  "/what-we-do/erp-implementations": { canonical: "/services/erp-implementations" },
  "/erp-implementations": { canonical: "/services/erp-implementations" },

  // ==========================================
  // AUTOMATION STACKS
  // ==========================================
  "/stack/manufacturing-automation": {
    title: "Manufacturing Automation Stack | Smart Factory Intelligence | Simpel",
    description:
      "Unify plant-floor IoT, SCADA, quality inspection, and supply chain logistics into a single cognitive manufacturing control center.",
    keywords: [
      "Manufacturing Automation Stack",
      "Smart Factory AI",
      "Industrial Automation",
      "Plant Floor Intelligence",
    ],
    canonical: "/stack/manufacturing-automation",
  },
  "/what-we-do/manufacturing-automation-stack": { canonical: "/stack/manufacturing-automation" },
  "/manufacturing-automation-stack": { canonical: "/stack/manufacturing-automation" },

  "/stack/sales-automation": {
    title: "Sales Automation Stack | AI-Powered Revenue Operations | Simpel",
    description:
      "Supercharge revenue teams with automated lead intelligence, relationship copilots, CRM enrichment, and predictive pipeline analytics.",
    keywords: [
      "Sales Automation Stack",
      "Revenue Intelligence",
      "AI CRM Copilot",
      "Sales AI",
    ],
    canonical: "/stack/sales-automation",
  },
  "/what-we-do/sales-automation-stack": { canonical: "/stack/sales-automation" },
  "/sales-automation-stack": { canonical: "/stack/sales-automation" },

  // ==========================================
  // PRODUCTS
  // ==========================================
  "/products/ek-rewards": {
    title: "Ek Rewards | AI-Powered Customer Loyalty & Engagement Platform | Simpel",
    description:
      "Drive customer retention, personalized rewards, and omnichannel engagement with Ek Rewards by Simpel.",
    keywords: ["Customer Loyalty AI", "Rewards Platform", "Ek Rewards", "Retention AI"],
    canonical: "/products/ek-rewards",
  },
  "/what-we-do/products/ek-rewards": { canonical: "/products/ek-rewards" },
  "/what-we-do/ek-rewards": { canonical: "/products/ek-rewards" },
  "/ek-rewards": { canonical: "/products/ek-rewards" },

  "/products/xp-skills": {
    title: "XP Skills | Workforce Upskilling & Talent Intelligence | Simpel",
    description:
      "Empower employees with adaptive, AI-driven learning paths, skill gap analysis, and real-time operational training with XP Skills.",
    keywords: ["Workforce AI", "Upskilling Platform", "XP Skills", "Employee Learning AI"],
    canonical: "/products/xp-skills",
  },
  "/what-we-do/products/xp-skills": { canonical: "/products/xp-skills" },
  "/what-we-do/xp-skills": { canonical: "/products/xp-skills" },
  "/xp-skills": { canonical: "/products/xp-skills" },

  "/products/simpel-erp": {
    title: "Simpel ERP | Next-Generation Cognitive Enterprise Resource Planning",
    description:
      "Modern, cloud-native ERP built with integrated cognitive AI for manufacturing, supply chain, financial management, and multi-entity operations.",
    keywords: ["Cognitive ERP", "Simpel ERP", "Cloud ERP", "Manufacturing ERP AI"],
    canonical: "/products/simpel-erp",
  },
  "/what-we-do/products/simpel-erp": { canonical: "/products/simpel-erp" },
  "/what-we-do/simpel-erp": { canonical: "/products/simpel-erp" },
  "/simpel-erp": { canonical: "/products/simpel-erp" },

  // ==========================================
  // INDUSTRY COGNITIVE AI COPILOTS
  // ==========================================
  "/cognitive/industries/financial-services/relationship-manager-copilot": {
    title: "Relationship Manager Copilot | Financial Services Intelligence | Simpel",
    description:
      "Help relationship managers prepare faster, personalize client conversations, and deepen trust with instant 360-degree financial intelligence.",
    keywords: [
      "Relationship Manager Copilot",
      "Wealth Management AI",
      "Banking AI Copilot",
      "Financial Services AI",
    ],
    canonical: "/cognitive/industries/financial-services/relationship-manager-copilot",
    ogImage: "/images/insights/rm-copilot-hero.jpg",
  },
  "/relationship-manager-copilot": {
    canonical: "/cognitive/industries/financial-services/relationship-manager-copilot",
  },
  "/cognitive/relationship-manager-copilot": {
    canonical: "/cognitive/industries/financial-services/relationship-manager-copilot",
  },

  "/cognitive/industries/financial-services/financial-research-assistant": {
    title: "Financial Research Assistant | Financial Services AI Copilot | Simpel",
    description:
      "Accelerate equity research, earnings transcript analysis, and portfolio risk assessments with Simpel's citation-backed Financial Research Assistant.",
    keywords: [
      "Financial Research Assistant",
      "Equity Research AI",
      "Financial Services Copilot",
      "Portfolio Analytics AI",
    ],
    canonical: "/cognitive/industries/financial-services/financial-research-assistant",
    ogImage: "/images/insights/the-challenge.jpg",
  },
  "/financial-research-assistant": {
    canonical: "/cognitive/industries/financial-services/financial-research-assistant",
  },
  "/industries/financial-services/financial-research-assistant": {
    canonical: "/cognitive/industries/financial-services/financial-research-assistant",
  },
  "/cognitive/financial-research-assistant": {
    canonical: "/cognitive/industries/financial-services/financial-research-assistant",
  },
  "/cognitive/industries/financial-services": {
    canonical: "/cognitive/industries/financial-services/financial-research-assistant",
  },

  "/cognitive/industries/manufacturing/quality-intelligence": {
    title: "Quality Intelligence | First-Pass Yield & Defect Reduction | Simpel",
    description:
      "Correlate production parameters with inspection data in real time. Detect defect patterns earlier and lift first-pass yield with Simpel Quality Intelligence.",
    keywords: [
      "Quality Intelligence",
      "Manufacturing Yield AI",
      "Defect Detection AI",
      "Factory Quality Control",
    ],
    canonical: "/cognitive/industries/manufacturing/quality-intelligence",
    ogImage: "/images/insights/quality-intelligence-hero.jpg",
  },
  "/quality-intelligence": {
    canonical: "/cognitive/industries/manufacturing/quality-intelligence",
  },
  "/cognitive/quality-intelligence": {
    canonical: "/cognitive/industries/manufacturing/quality-intelligence",
  },

  "/cognitive/industries/manufacturing/predictive-maintenance": {
    title: "Predictive Maintenance | Industrial AI & Downtime Prevention | Simpel",
    description:
      "Forecast machine wear, detect abnormal vibration signatures, and schedule repairs before failures occur with Simpel Predictive Maintenance.",
    keywords: [
      "Predictive Maintenance",
      "Industrial AI",
      "Equipment Failure Prevention",
      "Condition Monitoring AI",
    ],
    canonical: "/cognitive/industries/manufacturing/predictive-maintenance",
    ogImage: "/images/insights/predictive-maintenance-hero.jpg",
  },
  "/predictive-maintenance": {
    canonical: "/cognitive/industries/manufacturing/predictive-maintenance",
  },
  "/cognitive/predictive-maintenance": {
    canonical: "/cognitive/industries/manufacturing/predictive-maintenance",
  },

  "/cognitive/industries/manufacturing/production-copilot": {
    title: "Production Copilot | Shop Floor AI & Shift Optimization | Simpel",
    description:
      "Guide shop floor operators and shift supervisors with real-time bottleneck detection, line balancing, and automated shift handovers.",
    keywords: [
      "Production Copilot",
      "Shop Floor AI",
      "Manufacturing Operations",
      "Shift Optimization",
    ],
    canonical: "/cognitive/industries/manufacturing/production-copilot",
    ogImage: "/images/insights/production-copilot-hero.jpg",
  },
  "/production-copilot": {
    canonical: "/cognitive/industries/manufacturing/production-copilot",
  },
  "/cognitive/production-copilot": {
    canonical: "/cognitive/industries/manufacturing/production-copilot",
  },

  "/cognitive/industries/government-public-services/citizen-services-assistant": {
    title: "Citizen Services Assistant | Government & Public Sector AI | Simpel",
    description:
      "Guide citizens through public schemes, forms, and services with plain-language AI self-service, reducing contact center volume by 20–35%.",
    keywords: [
      "Citizen Services Assistant",
      "Public Sector AI",
      "Government Citizen Portal",
      "Public Services Self-Service",
    ],
    canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
    ogImage: "/images/insights/citizen-services-hero.jpg",
  },
  "/citizen-services-assistant": {
    canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
  },
  "/cognitive/citizen-services-assistant": {
    canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
  },
  "/cognitive/industries/citizen-services-assistant": {
    canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
  },

  "/cognitive/industries/government-public-services/government-knowledge-copilot": {
    title: "Government Knowledge Copilot | Public Sector Policy AI | Simpel",
    description:
      "Connect policies, regulations, circulars, and institutional knowledge so public-sector teams find relevant information faster with Simpel Government Knowledge Copilot.",
    keywords: [
      "Government Knowledge Copilot",
      "Public Sector Knowledge Management",
      "Government Policy AI",
      "Public Sector AI",
      "Institutional Knowledge Assistant",
    ],
    canonical: "/cognitive/industries/government-public-services/government-knowledge-copilot",
    ogImage: "/images/insights/government-knowledge-hero.jpg",
  },
  "/government-knowledge-copilot": {
    canonical: "/cognitive/industries/government-public-services/government-knowledge-copilot",
  },
  "/cognitive/government-knowledge-copilot": {
    canonical: "/cognitive/industries/government-public-services/government-knowledge-copilot",
  },
  "/cognitive/industries/government-knowledge-copilot": {
    canonical: "/cognitive/industries/government-public-services/government-knowledge-copilot",
  },

  "/cognitive/industries/healthcare/clinical-knowledge-assistant": {
    title: "Clinical Knowledge Assistant | Healthcare Provider AI | Simpel",
    description:
      "Equip doctors, nurses, and clinical specialists with instant, evidence-based care guidelines, protocols, and institutional medical insights.",
    keywords: [
      "Clinical Knowledge Assistant",
      "Healthcare AI",
      "Clinical Decision Support",
      "Medical Protocols AI",
    ],
    canonical: "/cognitive/industries/healthcare/clinical-knowledge-assistant",
    ogImage: "/images/insights/clinical-knowledge-hero.jpg",
  },
  "/clinical-knowledge-assistant": {
    canonical: "/cognitive/industries/healthcare/clinical-knowledge-assistant",
  },
  "/cognitive/clinical-knowledge-assistant": {
    canonical: "/cognitive/industries/healthcare/clinical-knowledge-assistant",
  },
  "/cognitive/industries/healthcare": {
    canonical: "/cognitive/industries/healthcare/clinical-knowledge-assistant",
  },

  "/cognitive/industries/healthcare/healthcare-operations-copilot": {
    title: "Healthcare Operations Copilot | Hospital Bed & Capacity AI | Simpel",
    description:
      "Predict patient admissions, streamline bed turns, and optimize nursing staffing ratios with Simpel Healthcare Operations Copilot.",
    keywords: [
      "Healthcare Operations Copilot",
      "Hospital Capacity AI",
      "Bed Management AI",
      "Patient Flow Optimization",
    ],
    canonical: "/cognitive/industries/healthcare/healthcare-operations-copilot",
    ogImage: "/images/insights/clinical-knowledge-hero.jpg",
  },
  "/healthcare-operations-copilot": {
    canonical: "/cognitive/industries/healthcare/healthcare-operations-copilot",
  },
  "/cognitive/healthcare-operations-copilot": {
    canonical: "/cognitive/industries/healthcare/healthcare-operations-copilot",
  },

  "/cognitive/industries/healthcare/patient-experience-copilot": {
    title: "Patient Experience Copilot | Healthcare Engagement & Care AI | Simpel",
    description:
      "Deliver 24/7 empathetic patient scheduling, pre-op instructions, and medication reminders with Simpel Patient Experience Copilot.",
    keywords: [
      "Patient Experience Copilot",
      "Healthcare Chatbot",
      "Patient Engagement AI",
      "Post-Care AI",
    ],
    canonical: "/cognitive/industries/healthcare/patient-experience-copilot",
    ogImage: "/images/insights/clinical-knowledge-hero.jpg",
  },
  "/patient-experience-copilot": {
    canonical: "/cognitive/industries/healthcare/patient-experience-copilot",
  },
  "/cognitive/patient-experience-copilot": {
    canonical: "/cognitive/industries/healthcare/patient-experience-copilot",
  },

  "/cognitive/industries/professional-services/knowledge-copilot": {
    title: "Knowledge Copilot | Professional Services Firm AI | Simpel",
    description:
      "Unify firm precedent, research memorandums, past pitches, and compliance knowledge into a secure, conversational workspace.",
    keywords: [
      "Knowledge Copilot",
      "Professional Services AI",
      "Legal Knowledge AI",
      "Consulting Knowledge Management",
    ],
    canonical: "/cognitive/industries/professional-services/knowledge-copilot",
    ogImage: "/images/insights/the-approach-bg.png",
  },
  "/knowledge-copilot": {
    canonical: "/cognitive/industries/professional-services/knowledge-copilot",
  },
  "/cognitive/industries/knowledge-copilot": {
    canonical: "/cognitive/industries/professional-services/knowledge-copilot",
  },

  "/cognitive/industries/professional-services/ai-research-assistant": {
    title: "AI Research Assistant | Deep Literature & Market Synthesis | Simpel",
    description:
      "Synthesize thousands of research papers, market surveys, and patents into actionable briefs with Simpel AI Research Assistant.",
    keywords: [
      "AI Research Assistant",
      "Literature Synthesis AI",
      "Market Intelligence Copilot",
      "Executive Research AI",
    ],
    canonical: "/cognitive/industries/professional-services/ai-research-assistant",
    ogImage: "/images/insights/ai-research-hero.jpg",
  },
  "/ai-research-assistant": {
    canonical: "/cognitive/industries/professional-services/ai-research-assistant",
  },
  "/cognitive/ai-research-assistant": {
    canonical: "/cognitive/industries/professional-services/ai-research-assistant",
  },

  "/cognitive/industries/professional-services/proposal-rfp-intelligence": {
    title: "Proposal & RFP Intelligence | Automated Bid Management | Simpel",
    description:
      "Draft compliant, high-scoring RFP responses 60% faster by automatically retrieving verified case studies, stats, and executive summaries.",
    keywords: [
      "Proposal RFP Intelligence",
      "RFP Automation AI",
      "Bid Management AI",
      "Tender Response AI",
    ],
    canonical: "/cognitive/industries/professional-services/proposal-rfp-intelligence",
    ogImage: "/images/insights/proposal-rfp-hero.jpg",
  },
  "/proposal-rfp-intelligence": {
    canonical: "/cognitive/industries/professional-services/proposal-rfp-intelligence",
  },
  "/cognitive/proposal-rfp-intelligence": {
    canonical: "/cognitive/industries/professional-services/proposal-rfp-intelligence",
  },

  // ==========================================
  // CASE STUDIES
  // ==========================================
  "/case-studies": {
    title: "Case Studies & Proven Impact | Real-World Enterprise Results | Simpel",
    description:
      "See how leading enterprises achieved 40-60% faster cycle times, 25-45% self-service lift, and measurable multi-million dollar ROI with Simpel Cognitive AI.",
    keywords: [
      "AI Case Studies",
      "Enterprise Automation Results",
      "Cognitive AI ROI",
      "Manufacturing AI Case Study",
    ],
    canonical: "/case-studies",
  },

  "/case-studies/skipper-limited": {
    title: "Skipper Limited Case Study | Digital Supply Chain & Plant Automation | Simpel",
    description:
      "How Skipper Limited transformed manufacturing and distribution operations using Simpel's end-to-end automation stack.",
    keywords: ["Skipper Limited Case Study", "Plant Automation", "Industrial AI Results"],
    canonical: "/case-studies/skipper-limited",
  },
  "/case-studies/skipper": { canonical: "/case-studies/skipper-limited" },
  "/skipper-limited": { canonical: "/case-studies/skipper-limited" },

  "/case-studies/financial-services": {
    title: "Financial Services AI Case Study | $14M Annual Value Creation | Simpel",
    description:
      "How a major commercial bank deployed Simpel Cognitive Copilots to cut loan underwriting cycle times by 55% with zero regulatory breaches.",
    keywords: ["Financial Services Case Study", "Banking AI ROI", "Commercial Lending Automation"],
    canonical: "/case-studies/financial-services",
    ogImage: "/images/insights/explore-insights-1.jpg",
  },
  "/insights/financial-services": { canonical: "/case-studies/financial-services" },
  "/case-study/finance-services": { canonical: "/case-studies/financial-services" },

  "/case-studies/automobile-tier-2-supplier": {
    title: "Automobile Tier-2 Supplier Case Study | 38% Defect Reduction | Simpel",
    description:
      "How Simpel Quality Intelligence delivered 38% lower rework, lifted first-pass yield, and generated $4.2M in annual cost savings.",
    keywords: ["Automobile Case Study", "Manufacturing Quality AI", "Defect Reduction ROI"],
    canonical: "/case-studies/automobile-tier-2-supplier",
    ogImage: "/images/insights/automobile-hero.jpg",
  },
  "/insights/automobile-tier-2-supplier": { canonical: "/case-studies/automobile-tier-2-supplier" },
  "/case-study/automobile-tier-2-supplier": { canonical: "/case-studies/automobile-tier-2-supplier" },

  "/case-studies/professional-services": {
    title: "Professional Services Case Study | 65% Faster RFP Cycles | Simpel",
    description:
      "How a global advisory firm leveraged Simpel Knowledge Copilots to shorten proposal response time from 14 days to 4 days.",
    keywords: ["Professional Services Case Study", "RFP Turnaround AI", "Consulting Knowledge AI"],
    canonical: "/case-studies/professional-services",
    ogImage: "/images/insights/professional-hero.jpg",
  },
  "/insights/professional-services": { canonical: "/case-studies/professional-services" },
  "/case-study/professional-services": { canonical: "/case-studies/professional-services" },

  // ==========================================
  // RESEARCH & INSIGHTS
  // ==========================================
  "/labs": {
    title: "Simpel Labs | Next-Gen AI Research, Sandbox & Innovations | Simpel",
    description:
      "Behind the scenes at Simpel Labs: Exploring frontier multimodal architectures, physical AI, real-time edge processing, and cognitive reasoning.",
    keywords: ["Simpel Labs", "AI R&D", "Frontier AI", "Physical AI Research"],
    canonical: "/labs",
  },

  "/insights": {
    title: "Insights & Thought Leadership | The Future of Cognitive AI | Simpel",
    description:
      "In-depth research, articles, and executive briefs on cognitive architectures, enterprise automation trends, and agentic workflows.",
    keywords: ["AI Insights", "Cognitive AI Blog", "Enterprise AI Trends"],
    canonical: "/insights",
  },

  // ==========================================
  // COMPANY & CAREERS
  // ==========================================
  "/about": {
    title: "About Us | Simpel — The Cognitive AI Company",
    description:
      "Learn about Simpel's mission to make enterprise operations simple, autonomous, and cognitive through human-centered artificial intelligence.",
    keywords: ["About Simpel", "Simpel AI Team", "Cognitive AI Leadership"],
    canonical: "/about",
  },

  "/careers": {
    title: "Careers at Simpel | Build the Future of Cognitive AI",
    description:
      "Join world-class AI engineers, systems architects, and product builders at Simpel. Explore open roles in machine learning, full-stack, and agentic systems.",
    keywords: ["AI Careers", "Machine Learning Jobs", "Simpel Careers", "Work in AI"],
    canonical: "/careers",
  },
  "/career": { canonical: "/careers" },

  "/careers/open-positions": {
    title: "Open Positions | Join the Simpel Team",
    description:
      "View all current job openings at Simpel across engineering, product, sales, and AI research.",
    keywords: ["Simpel Jobs", "AI Engineer Openings", "Tech Hiring"],
    canonical: "/careers/open-positions",
  },
  "/careers/roles": { canonical: "/careers/open-positions" },
  "/open-positions": { canonical: "/careers/open-positions" },

  // ==========================================
  // LEGAL & COMPLIANCE
  // ==========================================
  "/privacy-policy": {
    title: "Privacy Policy | Simpel",
    description:
      "Read Simpel's Privacy Policy to understand how we collect, use, and protect your personal and enterprise data.",
    canonical: "/privacy-policy",
    robots: "noindex, follow",
  },
  "/privacy": { canonical: "/privacy-policy" },
  "/legal": { canonical: "/privacy-policy" },

  "/terms-of-use": {
    title: "Terms of Use | Simpel",
    description:
      "Terms of Use and service agreements governing the use of Simpel products, websites, and cognitive AI platforms.",
    canonical: "/terms-of-use",
    robots: "noindex, follow",
  },
  "/terms": { canonical: "/terms-of-use" },

  "/cookie-policy": {
    title: "Cookie Policy | Simpel",
    description:
      "Information about how Simpel uses cookies and similar technologies on our website and digital services.",
    canonical: "/cookie-policy",
    robots: "noindex, follow",
  },
  "/cookies": { canonical: "/cookie-policy" },
};
